import { ArrowLeft, Send, Sun } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { PageSEO } from './PageSEO';

export function SolarQuotesPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const source = params.get('source') || 'direct';

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <PageSEO
        title="Get Solar Installation Quotes in Nigeria - Power.ng"
        description="Request quotes from verified Nigerian solar installers for homes, businesses, estates, and offices."
        path="/solar-quotes"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Power.ng Solar Quote Matching',
          serviceType: 'Solar installation quote request',
          areaServed: 'Nigeria',
          provider: {
            '@type': 'Organization',
            name: 'Power.ng',
            url: 'https://power.ng/',
          },
        }}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-[#008751] mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-8 items-start">
          <div className="bg-[#008751] text-white rounded-2xl p-8 shadow-lg">
            <div className="w-14 h-14 rounded-xl bg-white/15 flex items-center justify-center mb-5">
              <Sun className="w-8 h-8" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-4">
              Get Matched With Solar Installers
            </h1>
            <p className="text-white/90 leading-relaxed">
              Tell us what you need, your location, and your budget. Power.ng will use the details to route the request to relevant solar partners.
            </p>
            <div className="mt-8 space-y-3 text-sm text-white/90">
              <p>Best for homes, shops, offices, schools, clinics, and estates.</p>
              <p>No cash payments to third-party agents. Always verify quotes and warranties before paying an installer.</p>
            </div>
          </div>

          <form
            name="solar-quotes"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            className="bg-white rounded-2xl shadow-lg p-6 md:p-8"
          >
            <input type="hidden" name="form-name" value="solar-quotes" />
            <input type="hidden" name="source" value={source} />
            <p className="hidden">
              <label>
                Do not fill this out: <input name="bot-field" />
              </label>
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Full name</span>
                <input name="name" required className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#008751] focus:border-transparent" />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Phone number</span>
                <input name="phone" required inputMode="tel" className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#008751] focus:border-transparent" />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Email address</span>
                <input name="email" type="email" className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#008751] focus:border-transparent" />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">State</span>
                <input name="state" required className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#008751] focus:border-transparent" />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">City / area</span>
                <input name="city" required className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#008751] focus:border-transparent" />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Property type</span>
                <select name="property_type" required className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#008751] focus:border-transparent">
                  <option value="">Select one</option>
                  <option>Home / apartment</option>
                  <option>Shop / SME</option>
                  <option>Office</option>
                  <option>School / clinic</option>
                  <option>Estate / multi-building</option>
                </select>
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Monthly power spend</span>
                <select name="monthly_power_spend" className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#008751] focus:border-transparent">
                  <option value="">Select range</option>
                  <option>Under ₦50,000</option>
                  <option>₦50,000 - ₦150,000</option>
                  <option>₦150,000 - ₦500,000</option>
                  <option>Above ₦500,000</option>
                </select>
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Budget range</span>
                <select name="budget" required className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#008751] focus:border-transparent">
                  <option value="">Select range</option>
                  <option>Under ₦1 million</option>
                  <option>₦1m - ₦3m</option>
                  <option>₦3m - ₦7m</option>
                  <option>Above ₦7m</option>
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="block text-sm font-medium text-gray-700 mb-2">What do you want powered?</span>
                <textarea name="desired_system" rows={4} required className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#008751] focus:border-transparent" placeholder="Example: lights, fans, fridge, freezer, TV, 1 AC, office equipment..." />
              </label>
              <label className="block md:col-span-2">
                <span className="block text-sm font-medium text-gray-700 mb-2">How soon do you need it?</span>
                <select name="urgency" required className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:ring-2 focus:ring-[#008751] focus:border-transparent">
                  <option value="">Select timing</option>
                  <option>Immediately</option>
                  <option>Within 30 days</option>
                  <option>Within 3 months</option>
                  <option>Researching options</option>
                </select>
              </label>
            </div>

            <button type="submit" className="mt-6 w-full bg-[#008751] text-white py-4 rounded-lg font-bold hover:bg-[#006d41] transition-colors inline-flex items-center justify-center gap-2">
              Submit Quote Request
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
